import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Download, Calendar, FileSpreadsheet, Filter, 
  CheckCircle2, XCircle, Clock, PoundSterling
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

interface InvoiceExportProps {
  clientId?: string;
}

export const InvoiceExport: React.FC<InvoiceExportProps> = ({ clientId }) => {
  const { toast } = useToast();
  const [fromDate, setFromDate] = useState<Date>(startOfMonth(subMonths(new Date(), 1)));
  const [toDate, setToDate] = useState<Date>(endOfMonth(new Date()));
  const [selectedClientId, setSelectedClientId] = useState<string>(clientId || 'all');
  const [includeStatus, setIncludeStatus] = useState({
    completed: true,
    cancelled: true,
    no_show: true
  });
  const [isExporting, setIsExporting] = useState(false);

  const { data: clients } = useQuery({
    queryKey: ['clinician-clients-list'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: clientsData, error } = await supabase
        .from('clinician_clients')
        .select('id, client_id')
        .eq('clinician_id', user.id)
        .eq('status', 'active');

      if (error) throw error;

      // Fetch profiles separately
      if (clientsData && clientsData.length > 0) {
        const clientIds = clientsData.map(c => c.client_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, display_name')
          .in('user_id', clientIds);

        const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
        return clientsData.map(client => ({
          ...client,
          profile: profileMap.get(client.client_id)
        }));
      }
      return clientsData || [];
    },
    enabled: !clientId
  });

  const { data: sessions, isLoading } = useQuery({
    queryKey: ['export-sessions', fromDate, toDate, selectedClientId, includeStatus],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        .from('therapy_sessions')
        .select('*')
        .eq('clinician_id', user.id)
        .gte('session_date', format(fromDate, 'yyyy-MM-dd'))
        .lte('session_date', format(toDate, 'yyyy-MM-dd'))
        .order('session_date', { ascending: true });

      if (selectedClientId !== 'all') {
        query = query.eq('client_id', selectedClientId);
      }

      // Filter by status
      const statuses: string[] = [];
      if (includeStatus.completed) statuses.push('completed');
      if (includeStatus.cancelled) statuses.push('cancelled');
      if (includeStatus.no_show) statuses.push('no_show');
      
      if (statuses.length > 0 && statuses.length < 3) {
        query = query.in('status', statuses);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch client profiles
      const clientIds = [...new Set(data?.filter(s => s.client_id).map(s => s.client_id))];
      let profileMap = new Map();
      
      if (clientIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, display_name')
          .in('user_id', clientIds);
        
        profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
      }

      return data?.map(session => ({
        ...session,
        profile: session.client_id ? profileMap.get(session.client_id) : null
      })) || [];
    }
  });

  const calculateTotals = () => {
    if (!sessions) return { total: 0, completed: 0, cancelled: 0, no_show: 0 };

    return sessions.reduce((acc, session: any) => {
      const fee = session.session_fee || 0;
      acc.total += fee;
      
      if (session.status === 'completed') {
        acc.completed += fee;
      } else if (session.status === 'cancelled') {
        acc.cancelled += fee;
      } else if (session.status === 'no_show') {
        acc.no_show += fee;
      }
      
      return acc;
    }, { total: 0, completed: 0, cancelled: 0, no_show: 0 });
  };

  const handleExport = async () => {
    if (!sessions || sessions.length === 0) {
      toast({
        title: 'No data to export',
        description: 'There are no sessions matching your filters.',
        variant: 'destructive'
      });
      return;
    }

    setIsExporting(true);

    try {
      // Create CSV content
      const headers = [
        'Date',
        'Time',
        'Client Name',
        'Client Email',
        'Session Type',
        'Duration (mins)',
        'Status',
        'Fee (£)',
        'Paid',
        'Notes'
      ];

      const rows = sessions.map((session: any) => [
        format(new Date(session.session_date), 'dd/MM/yyyy'),
        session.session_time?.slice(0, 5) || '',
        session.profile?.display_name || session.client_name || 'Unknown',
        '',
        session.session_type || 'Individual',
        session.duration_minutes || 60,
        session.status,
        (session.session_fee || 0).toFixed(2),
        session.payment_status === 'paid' ? 'Yes' : 'No',
        (session.session_notes || '').replace(/"/g, '""')
      ]);

      // Add totals row
      const totals = calculateTotals();
      rows.push([]);
      rows.push(['Summary']);
      rows.push(['Total Sessions', sessions.length.toString()]);
      rows.push(['Completed Sessions', sessions.filter(s => s.status === 'completed').length.toString()]);
      rows.push(['Cancelled Sessions', sessions.filter(s => s.status === 'cancelled').length.toString()]);
      rows.push(['No Shows', sessions.filter(s => s.status === 'no_show').length.toString()]);
      rows.push(['Total Fees', `£${totals.total.toFixed(2)}`]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `therapy-sessions-${format(fromDate, 'yyyyMMdd')}-${format(toDate, 'yyyyMMdd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Export complete',
        description: `Exported ${sessions.length} sessions to CSV.`,
      });
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description: error.message || 'Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsExporting(false);
    }
  };

  const totals = calculateTotals();

  return (
    <Card className="border-border/50">
      <CardHeader>
        <div className="flex items-center gap-3">
          <FileSpreadsheet className="w-5 h-5 text-primary" />
          <div>
            <CardTitle className="text-lg">Export for Invoicing</CardTitle>
            <CardDescription>
              Download session records with fees for your accounting
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Date Range Selection */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>From Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <Calendar className="mr-2 h-4 w-4" />
                  {format(fromDate, 'd MMMM yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={fromDate}
                  onSelect={(date) => date && setFromDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>To Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <Calendar className="mr-2 h-4 w-4" />
                  {format(toDate, 'd MMMM yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={toDate}
                  onSelect={(date) => date && setToDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Client Filter */}
        {!clientId && clients && (
          <div className="space-y-2">
            <Label>Client</Label>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger>
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients?.map((client: any) => (
                  <SelectItem key={client.client_id} value={client.client_id}>
                    {client.profile?.display_name || 'Client'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Status Filters */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Include Session Status
          </Label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={includeStatus.completed}
                onCheckedChange={(checked) => 
                  setIncludeStatus(prev => ({ ...prev, completed: !!checked }))
                }
              />
              <span className="text-sm flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-green-500" />
                Completed
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={includeStatus.cancelled}
                onCheckedChange={(checked) => 
                  setIncludeStatus(prev => ({ ...prev, cancelled: !!checked }))
                }
              />
              <span className="text-sm flex items-center gap-1">
                <XCircle className="w-3 h-3 text-amber-500" />
                Cancelled
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={includeStatus.no_show}
                onCheckedChange={(checked) => 
                  setIncludeStatus(prev => ({ ...prev, no_show: !!checked }))
                }
              />
              <span className="text-sm flex items-center gap-1">
                <Clock className="w-3 h-3 text-red-500" />
                No Show
              </span>
            </label>
          </div>
        </div>

        {/* Preview Summary */}
        <div className="bg-muted/30 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-foreground flex items-center gap-2">
            <PoundSterling className="w-4 h-4" />
            Export Summary
          </h4>
          
          {isLoading ? (
            <div className="animate-pulse space-y-2">
              <div className="h-4 bg-muted rounded w-1/2" />
              <div className="h-4 bg-muted rounded w-1/3" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Total Sessions:</span>
                  <span className="ml-2 font-medium text-foreground">{sessions?.length || 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Total Fees:</span>
                  <span className="ml-2 font-medium text-foreground">£{totals.total.toFixed(2)}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Completed: £{totals.completed.toFixed(2)}
                </Badge>
                <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                  <XCircle className="w-3 h-3 mr-1" />
                  Cancelled: £{totals.cancelled.toFixed(2)}
                </Badge>
                <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">
                  <Clock className="w-3 h-3 mr-1" />
                  No Show: £{totals.no_show.toFixed(2)}
                </Badge>
              </div>
            </>
          )}
        </div>

        {/* Export Button */}
        <Button 
          className="w-full bg-primary hover:bg-primary/90"
          onClick={handleExport}
          disabled={isExporting || isLoading || !sessions?.length}
        >
          <Download className="w-4 h-4 mr-2" />
          {isExporting ? 'Exporting...' : 'Download CSV'}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          Export includes date, time, client details, session type, duration, status, fee, and payment status.
        </p>
      </CardContent>
    </Card>
  );
};

export default InvoiceExport;
